import Big from 'big.js';
import { ColumnStats } from 'smg/admin/hyejoo';
import axios from 'smg/axios';
import Batch from 'smg/batch';
import { Start, Stop } from 'smg/buttons';
import * as hyejoo from 'smg/customer-api/hyejoo';
import * as session from 'smg/customer-api/session';
import * as models from 'smg/models';
import { GetProofURLs } from 'smg/proofs';
import { G } from 'smg/querystring';
import { Clone } from 'smg/template';
import { DollarFormat, RemToPx } from 'smg/util';
import { PageLoader } from '../../account';
import { AddCartItem } from '../../carts';
import { Relref } from '../../hugo';
import { GetOrderStatus } from '../../order';
import { MakePayment } from '../../payments';
import { SwiperInit } from '../../swiper';

declare interface myWindow extends Window {
    readonly cloudFront: string;
    googleReviews: string;
}
declare const window: myWindow;

if (document.body.id === 'account-orders') {
    void main();
}

const pageLoaders = new Map<string, PageLoader<models.Order>>();
let currentPageLoader: PageLoader<models.Order> | null = null;

export async function main() {
    if (await session.Redirect(undefined, Relref('/'))) {
        return;
    }

    const countPromises: Promise<number>[] = [];

    const b = new Batch();

    const allOrdersCount = b.get<ColumnStats>('/orders', {
        params: {
            'colStats': 'id',
        },
    }).then(resp => resp.data.count ?? 0);

    const awaitingPaymentOrdersCount = b.get<ColumnStats>('/orders', {
        params: {
            'colStats': 'id',
            'q': 'balance:>.05',
        },
    }).then(resp => resp.data.count ?? 0);

    const inProductionOrdersCount = b.get<ColumnStats>('/orders', {
        params: {
            'colStats': 'id',
            'q': `orderStatusID:${models.OrderStatusID.ORDER_STATUSES_CONFIRMED} hasShipments:false balance:<.05`,
        },
    }).then(resp => resp.data.count ?? 0);

    const onTheWayOrdersCount = b.get<ColumnStats>('/orders', {
        params: {
            'colStats': 'id',
            'q': 'shipmentsToCustomerNotDeliveredCount:>0 balance:<.05',
        },
    }).then(resp => resp.data.count ?? 0);

    const deliveredOrdersCount = b.get<ColumnStats>('/orders', {
        params: {
            'colStats': 'id',
            'q': 'flagIDs:"Delivered" shipmentsToCustomerNotDeliveredCount:0',
        },
    }).then(resp => resp.data.count ?? 0);

    countPromises.push(allOrdersCount, awaitingPaymentOrdersCount, inProductionOrdersCount, onTheWayOrdersCount, deliveredOrdersCount);

    const carriers = b.get<hyejoo.Carriers>('/carriers', {
        params: {
            'data': 'name,link',
        },
    }).then(resp => {
        const m = new Map<number, models.Carrier>();
        for (const carrier of resp.data.carriers) {
            m.set(carrier.id ?? 0, carrier);
        }
        return m;
    });

    const lineItems = b.get<hyejoo.LineItems>('/v2/line-items', {
        params: {
            'data': 'name,pluralName,product',
        },
    }).then(resp => {
        const m = new Map<string, models.LineItem>();
        for (const lineItem of resp.data.lineItems) {
            m.set(lineItem.id ?? '', lineItem);
        }
        return m;
    });

    void b.Exec();

    await Promise.all(countPromises).then(async () => {
        const $statusNav = document.querySelector('#status-nav') as HTMLElement;
        $statusNav.classList.remove('d-none');

        if (await allOrdersCount === 0) {
            drawEmptyResults(document.querySelector('.tab-content') as HTMLElement);
        }
    });

    const $allOrdersCount = document.querySelector('#all-orders-count') as HTMLElement;
    $allOrdersCount.textContent = `(${await allOrdersCount})`;
    const $inProductionOrdersCount = document.querySelector('#in-production-orders-count') as HTMLElement;
    $inProductionOrdersCount.textContent = `(${await inProductionOrdersCount})`;
    const $deliveredOrdersCount = document.querySelector('#delivered-orders-count') as HTMLElement;
    $deliveredOrdersCount.textContent = `(${await deliveredOrdersCount})`;
    const $onTheWayOrdersCount = document.querySelector('#on-the-way-orders-count') as HTMLElement;
    $onTheWayOrdersCount.textContent = `(${await onTheWayOrdersCount})`;
    const $awaitingPaymentOrdersCount = document.querySelector('#awaiting-payment-orders-count') as HTMLElement;
    $awaitingPaymentOrdersCount.textContent = `(${await awaitingPaymentOrdersCount})`;

    const $cardTemplate = document.querySelector('#order-card-template') as HTMLTemplateElement;

    const $allOrdersTabPane = document.querySelector('#all-orders-tab-pane') as HTMLElement;
    const $allOrdersContainer = $allOrdersTabPane.querySelector('.inner-container') as HTMLElement;

    G().Delete('p');

    const $tabPaneTriggers = Array.from(document.querySelectorAll<HTMLElement>('[data-bs-toggle="tab"]'));
    const allOrdersPl = createPl($allOrdersContainer, await allOrdersCount);
    pageLoaders.set('#all-orders-tab-pane', allOrdersPl);
    currentPageLoader = allOrdersPl;
    currentPageLoader.Activate();
    currentPageLoader.Load();

    for (const $tabPaneTrigger of $tabPaneTriggers) {
        $tabPaneTrigger.addEventListener('show.bs.tab', async () => {
            const target = $tabPaneTrigger.getAttribute('data-bs-target');
            if (!target) {
                return;
            }

            let $parent: HTMLElement | undefined;

            const $tabPane = document.querySelector(target) as HTMLElement;
            if ($tabPane) {
                $parent = $tabPane.querySelector('.inner-container') as HTMLElement;
            }

            if (currentPageLoader) {
                currentPageLoader.Deactivate();
            }

            let pl = pageLoaders.get(target);

            if (!pl) {
                G().Delete('p');
                if ($parent) {
                    $parent.innerHTML = '';
                }

                switch (target) {
                case '#all-orders-tab-pane':
                    pl = createPl($parent, await allOrdersCount);
                    break;
                case '#awaiting-payment-tab-pane':
                    pl = createPl($parent, await awaitingPaymentOrdersCount, { q: 'balance:>.05' });
                    break;
                case '#in-production-tab-pane':
                    pl = createPl($parent, await inProductionOrdersCount, { q: `orderStatusID:${models.OrderStatusID.ORDER_STATUSES_CONFIRMED} hasShipments:false` });
                    break;
                case '#delivered-tab-pane':
                    pl = createPl($parent, await deliveredOrdersCount, { q: 'flagIDs:"Delivered" shipmentsToCustomerNotDeliveredCount:0' });
                    break;
                case '#on-the-way-tab-pane':
                    pl = createPl($parent, await onTheWayOrdersCount, { q: 'shipmentsToCustomerNotDeliveredCount:>0' });
                    break;
                default:
                    return;
                }
                if (pl) {
                    pageLoaders.set(target, pl);
                }
            }

            if (pl) {
                currentPageLoader = pl;
                currentPageLoader.Activate();
                currentPageLoader.Load();
            }
        });
    }

    function createPl($parent: HTMLElement | undefined, count: number = 0, params?: Record<string, string | number>): PageLoader<models.Order> {
        const $viewMoreBtnTemplate = Clone('#view-more-btn-template').firstElementChild as HTMLElement;
        const $viewMoreBtn = $viewMoreBtnTemplate?.querySelector('#view-more-btn') as HTMLButtonElement;

        const $currMax = $viewMoreBtnTemplate?.querySelector('#current-max') as HTMLElement;
        const $totalTabResults = $viewMoreBtnTemplate?.querySelector('#total-tab-results') as HTMLElement;

        let nextPage = true;
        let emptyResults = false;

        const query = params?.['q'];

        const pl: PageLoader<models.Order> = new PageLoader<models.Order>({
            clearResults: false,
            parent: $parent,
            nextBtn: $viewMoreBtn,
            navTabs: $tabPaneTriggers,
            get: () => axios.get<hyejoo.Orders>('/orders', {
                params: {
                    'sort': params?.['sort'] || 'created desc',
                    'max': params?.['max'] || 5,
                    'data': params?.['data'] || 'invoiceNumber,created,orderLineItems,orderProofs,orderShippingOptions,orderPayments,total,paid,balance,totalSansTax,orderStatusID,taxExemption,shipmentCount',
                    'p': params?.['p'] || pl.CurrentPage(),
                    'q': query && pl.Search() ? `${query} invoiceNumber:${pl.Search()}` : query || pl.Search(),
                },
            }).then(resp => {
                if (resp.data.orders.length === 0) {
                    emptyResults = true;
                }
                if (!resp.data.nextPage) {
                    nextPage = false;
                } else {
                    nextPage = true;
                    $viewMoreBtnTemplate?.classList.remove('d-none');
                }
                return { rows: resp.data.orders, nextPage: resp.data.nextPage };
            }),
            draw: async orders => {
                const card = Clone($cardTemplate);
                const $btnContainer = card.querySelector<HTMLElement>('.btn-container');
                const $orderDetailsContainer = card.querySelector<HTMLElement>('.order-details-container');

                // Order Invoice
                const $invoice = card.querySelector<HTMLAnchorElement>('.invoice');
                if ($invoice) {
                    $invoice.textContent = `Order #: ${String(orders.invoiceNumber) || ''}`;
                    $invoice.href = `${Relref('/account/order')}?id=${orders.id || ''}`;
                }

                // Order Date
                const $orderDates = Array.from(card.querySelectorAll<HTMLElement>('.order-date'));
                for (const $orderDate of $orderDates) {
                    if ($orderDate && orders.created) {
                        $orderDate.textContent = new Date(orders.created).toLocaleDateString();
                    }
                }

                // Tax Exemption
                const $taxExemption = card.querySelector<HTMLElement>('.tax-exemption');
                if ($taxExemption) {
                    $taxExemption.textContent = orders.userTaxExemptionOrders?.length ? 'Yes' : 'No';
                }

                // Order Status
                const $status = card.querySelector<HTMLElement>('.status');
                if ($status) {
                    const { status, tag } = GetOrderStatus(orders);
                    $status.textContent = status;
                    $status.classList.add(tag);

                    if (status === 'Delivered') {
                        const $btn = Clone('#rate-us-btn-template').firstElementChild as HTMLElement;
                        const $a = $btn?.querySelector('a') as HTMLAnchorElement;
                        $a.href = window.googleReviews || '';
                        $btnContainer?.insertBefore($btn, $btnContainer.lastElementChild);
                    }
                }

                // Images
                const $imagesContainer = card.querySelector<HTMLElement>('.swiper-container');
                if ($imagesContainer) {
                    if (orders.orderProofs?.length) {
                        const widths = ['7.5rem', '17rem', '18.75rem'];
                        let i = 0;

                        for (const { proof } of orders.orderProofs) {
                            if (!proof) {
                                continue;
                            }

                            const $swiper = $imagesContainer.querySelector<HTMLElement>('.swiper');
                            if ($swiper && i < widths.length) {
                                $swiper.style.width = widths[i];
                            }

                            i++;

                            const urls = GetProofURLs(window.cloudFront, proof.file || '', proof.path || '');

                            const el = Clone('#order-proof-template');

                            const a = el.querySelector('a') as HTMLAnchorElement;
                            a.href = urls.main;
                            a.setAttribute('data-fancybox', orders.id ?? '');

                            const img = a.querySelector('img') as HTMLImageElement;
                            img.src = urls.thumb;
                            img.alt = proof.name || '';
                            img.style.maxWidth = '7.5rem';
                            img.style.maxHeight = '7.5rem';

                            const $swiperContainer = $imagesContainer.querySelector<HTMLElement>('.swiper-wrapper');
                            $swiperContainer?.appendChild(a);
                        }
                    } else {
                        $orderDetailsContainer?.classList.remove('ps-4');
                    }
                }

                // Order Details
                if ($orderDetailsContainer) {
                    if (orders.orderLineItems?.length) {
                        const qtys: Record<string, number> = {};
                        for (const ol of orders.orderLineItems) {
                            if (!ol.quantity) {
                                continue;
                            }

                            const lineItem = (await lineItems).get(ol.lineItemID || '');
                            if (!lineItem?.product) {
                                continue;
                            }
                            if (!ol.lineItemModel) {
                                continue;
                            }

                            const name = ol.lineItem || (lineItem as models.LineItem).pluralName || (lineItem as models.LineItem).name || 'Other';
                            qtys[name] = (qtys[name] || 0) + ol.quantity;
                        }

                        Object.keys(qtys).forEach(n => {
                            const $span = Clone('#line-item-qty').firstElementChild;
                            if ($span) {
                                $span.textContent = `${qtys[n]} ${n}`;
                                if ($orderDetailsContainer.firstElementChild?.lastElementChild) {
                                    $orderDetailsContainer.firstElementChild.insertBefore($span, $orderDetailsContainer.firstElementChild.lastElementChild);
                                }
                            }
                        });
                    }

                    if (orders.total) {
                        const $total = $orderDetailsContainer.querySelector<HTMLElement>('.total');
                        if ($total) {
                            $total.textContent = DollarFormat(orders.total);
                        }
                    }

                    const toggleBtnClasses = () => {
                        const $reorderBtnSm = $btnContainer?.querySelector<HTMLElement>('.reorder-link');
                        const $innerBtnContainer = $btnContainer?.querySelector<HTMLElement>('.inner-btn-container');
                        $innerBtnContainer?.classList.remove('flex-column');
                        if ($reorderBtnSm) {
                            $reorderBtnSm.classList.remove('w-100');
                            $reorderBtnSm.classList.add('flex-grow-1');
                        }
                        if ($viewOrderDetails) {
                            $viewOrderDetails.classList.remove('w-100');
                            $viewOrderDetails.classList.add('flex-grow-1');
                        }
                    };

                    // Re-order link
                    const $reorderBtns = Array.from(card.querySelectorAll<HTMLElement>('.reorder-link'));
                    for (const $reorderBtn of $reorderBtns) {
                        $reorderBtn?.addEventListener('click', async () => {
                            Start($reorderBtn);
                            await AddCartItem({ orderID: orders.id ?? '' }).finally(() => Stop($reorderBtn));
                        });
                    }

                    // View Order Details Btn
                    const $viewOrderDetails = card.querySelector<HTMLAnchorElement>('.view-order-details-btn');
                    if ($viewOrderDetails) {
                        $viewOrderDetails.href = `${Relref('/account/order')}?id=${orders.id || ''}`;
                    }

                    // Tracking link
                    if (orders.orderShippingOptions?.length) {
                        if (orders.orderShippingOptions[0].orderShippingOptionShipments?.length) {
                            const shipment = orders.orderShippingOptions[0].orderShippingOptionShipments[0].shipment;
                            if (shipment) {
                                const carrier = (await carriers).get(shipment.carrierID ?? 0);

                                if (carrier) {
                                    toggleBtnClasses();
                                    const $btn = Clone('#tracking-link-btn-template').firstElementChild as HTMLElement;
                                    const a = $btn?.querySelector('a') as HTMLAnchorElement;
                                    a.href = (carrier.link ?? '').replace('%%', shipment.trackingNumber ?? '');
                                    a.setAttribute('target', '_blank');

                                    $btnContainer?.insertBefore($btn, $btnContainer.lastElementChild);
                                }
                            }
                        }
                    }

                    // Pay Balance Btn
                    if (orders.balance && Big(orders.balance).gt(Big(.05))) {
                        toggleBtnClasses();
                        const $payBalanceBtn = Clone('#pay-balance-link-btn-template').firstElementChild as HTMLElement;
                        const $btn = $payBalanceBtn?.querySelector('button') as HTMLButtonElement;
                        $btn.textContent = `Pay Balance ${DollarFormat(orders.balance ?? 0)}`;
                        $btn.addEventListener('click', async () => {
                            await MakePayment(await session.Session(), orders);
                        });
                        $btnContainer?.insertBefore($btn, $btnContainer.lastElementChild);
                    }
                }

                return card;
            },
            afterLoad: () => {
                SwiperInit({ slidesPerView: 2, spaceBetween: RemToPx(.5) });

                if (emptyResults) {
                    drawEmptyResults($parent as HTMLElement);
                    emptyResults = false;
                }

                if (nextPage) {
                    $parent?.appendChild($viewMoreBtnTemplate);
                    $viewMoreBtnTemplate.classList.remove('d-none');
                } else {
                    $viewMoreBtnTemplate.classList.add('d-none');
                }

                $currMax.textContent = String((pl.CurrentPage() + 1) * (typeof params?.['max'] === 'number' ? params['max'] : 5));
                $totalTabResults.textContent = String(count);
            },
        });
        return pl;
    }
}

function drawEmptyResults($parent: HTMLElement) {
    $parent.innerHTML = '';
    const $empty = Clone('#empty-results').firstElementChild as HTMLElement;
    $parent.appendChild($empty);
}